import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import useIsPathToSubmissionDisabled from 'app/pages/loanApplication/hooks/useIsPathToSubmissionDisabled';
import useIsPrepareDisclosuresWizardDisabled from 'app/pages/loanApplication/hooks/useIsPrepareDisclosuresWizardDisabled';
import useResolvedDisclosuresSendAction from 'app/pages/loanApplication/hooks/useResolvedDisclosuresSendAction';
import { ApplicationStatus } from 'app/pages/loanApplication/LoanApplication.constants';
import {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/EditModal.constants';
import { LOAN_ACTION_TRIGGER_EVENT } from 'app/pages/loanApplication/UniversalSearch/UniversalSearch.constants';
import { ArrowIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';

import { LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { DocumentsSubtabEnum, LoanApplicationTabName } from 'constants/loanApplication';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectIsBrokerOrSubCompanyUser } from 'store/auth/selectors';
import {
  getAusCasefilePrefillAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanBundleHistoryAction,
  getLoanConditionByLoanIdAction,
  openPrepareDisclosuresWizardAction,
  setActiveTabWithNavigationAction,
  startWholesaleAusSubmitAction,
} from 'store/loanApplications/actions';
import {
  selectActiveChannelConfig,
  selectApplicationIdFromDeal,
  selectApplicationStatusFromDeal,
  selectAusCasefileIntake,
  selectAusSubmitError,
  selectAusSummary,
  selectIsAusApproved,
  selectIsAusApproveEligible,
  selectIsAusSubmitting,
  selectIsAusSummaryExist,
  selectIsAusSummarySettled,
  selectIsDisclosureSent,
  selectIsLoanBundleHistorySettled,
  selectIsLoanConditionsSettled,
  selectIsLoanLocked,
  selectLoanActions,
  selectLoanIdFromDeal,
  selectLoanSubCompanyId,
  selectOutstandingConditionsCount,
} from 'store/loanApplications/selectors';
import {
  selectAreCommonInfoRequestsSettled,
  selectCommonInfoLoanApplication,
} from 'store/loanApplications/selectors/loanApplicationCommonInfo.selectors';

import AusCasefileIntakeDrawer from './AusCasefileIntakeDrawer';
import NextActionCallout from './NextActionCallout';
import {
  AUS_LEG,
  NEXT_ACTION_SEVERITY,
  STEP_KEY,
  STEP_SUBTITLE,
  STEP_TITLE,
  TERMINAL_ACTION_BUTTON_LABEL,
  TERMINAL_ACTION_LOAN_ACTION,
  WHOLESALE_AUS_BADGE,
  WHOLESALE_AUS_CTA_LABEL,
  WHOLESALE_AUS_EDIT_CTA_LABEL,
  WHOLESALE_AUS_ERROR_FALLBACK_DESCRIPTION,
  WHOLESALE_AUS_ERROR_TITLE,
  WHOLESALE_AUS_IN_PROGRESS_TITLE,
  WHOLESALE_AUS_NEXT_ACTION,
  WHOLESALE_AUS_SUBTITLE,
} from './PathToSubmission.constants';
import {
  calloutChipsRowStyles,
  calloutSectionStyles,
  detailSectionStyles,
  getChevronButtonStyles,
  progressTextGroupStyles,
  terminalButtonStyles,
  widgetStyles,
} from './PathToSubmission.styles';
import { PathToSubmissionTestIds } from './PathToSubmission.testIds';
import {
  areStepSignalsResolved,
  buildAusRetryLabel,
  buildNextAction,
  buildRailSteps,
  buildStepStatusMap,
  buildSubmissionSteps,
  buildWholesaleAusChips,
  buildWholesaleAusInProgressDescription,
  computeDonePercent,
  countStepProgress,
  deriveActiveStepKey,
  findLoanActionByType,
  parseAusLegErrors,
} from './PathToSubmission.utils';
import PathToSubmissionHeader from './PathToSubmissionHeader';
import PathToSubmissionSkeleton from './PathToSubmissionSkeleton';
import SubmissionProgressBar from './SubmissionProgressBar';
import SubmissionStepper from './SubmissionStepper';
import SubmissionStepRow from './SubmissionStepRow';

const EMPTY_CHIPS = [];

const PathToSubmission = () => {
  const dispatch = useDispatch();

  const channelConfig = useSelector(selectActiveChannelConfig);
  const applicationId = useSelector(selectApplicationIdFromDeal);
  const applicationStatus = useSelector(selectApplicationStatusFromDeal);
  const loanId = useSelector(selectLoanIdFromDeal);
  const isBrokerOrSubCompanyUser = useSelector(selectIsBrokerOrSubCompanyUser);
  const isLoanLocked = useSelector(selectIsLoanLocked);
  const isAusDone = useSelector(selectIsAusSummaryExist);
  const isAusApproved = useSelector(selectIsAusApproved);
  const isAusApproveEligible = useSelector(selectIsAusApproveEligible);
  const outstandingConditionsCount = useSelector(selectOutstandingConditionsCount);
  const isDisclosureSent = useSelector(selectIsDisclosureSent);
  const commonInfo = useSelector(selectCommonInfoLoanApplication);
  const loanActions = useSelector(selectLoanActions);
  const subCompanyId = useSelector(selectLoanSubCompanyId);
  const ausSummary = useSelector(selectAusSummary);
  const isAusSubmitting = useSelector(selectIsAusSubmitting);
  const casefileIntake = useSelector(selectAusCasefileIntake);
  const ausSubmitError = useSelector(selectAusSubmitError);

  const areCommonInfoRequestsSettled = useSelector(selectAreCommonInfoRequestsSettled);
  const isAusSummarySettled = useSelector(selectIsAusSummarySettled);
  const isConditionsSettled = useSelector(selectIsLoanConditionsSettled);
  const isDisclosureHistorySettled = useSelector(selectIsLoanBundleHistorySettled);

  const companyId = useCompanyId();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isCasefileDrawerOpen, setIsCasefileDrawerOpen] = useState(false);

  const isWholesale = !!subCompanyId;

  const isPathToSubmissionDisabled = useIsPathToSubmissionDisabled();
  const isPrepareDisclosuresWizardDisabled = useIsPrepareDisclosuresWizardDisabled();

  // The widget renders only for registered, channel-driven loans on a tenant that has not opted
  // out; only then is it worth eagerly fetching the conditions + disclosure signals it needs.
  const isWidgetVisible =
    !isPathToSubmissionDisabled &&
    !!channelConfig &&
    applicationStatus === ApplicationStatus.REGISTERED;
  const disclosureStepEnabled = !!channelConfig?.disclosureStepEnabled;

  // A null loanId short-circuits the hook's fetch, so a hidden widget, channels without a
  // disclosures step, and tenants that disabled the wizard never pay for a bundle catalog
  // they cannot use.
  const { sendAction: disclosuresSendAction, isLoading: isResolvingDisclosuresSendAction } =
    useResolvedDisclosuresSendAction(
      isWidgetVisible && disclosureStepEnabled && !isPrepareDisclosuresWizardDisabled
        ? loanId
        : null,
    );

  const isPricingDone = !!commonInfo?.programId;
  const signals = useMemo(
    () => ({
      isPricingDone,
      isLockDone: isLoanLocked,
      isAusDone,
      isAusApproved,
      isAusApproveEligible,
      ausCompletionRequirement: channelConfig?.ausCompletionRequirement,
      outstandingConditionsCount,
      isDisclosureSent,
    }),
    [
      isPricingDone,
      isLoanLocked,
      isAusDone,
      isAusApproved,
      isAusApproveEligible,
      channelConfig?.ausCompletionRequirement,
      outstandingConditionsCount,
      isDisclosureSent,
    ],
  );

  // Conditions are otherwise loaded only by the Underwriting tab; the persistent
  // widget fetches them itself so the "Conditions cleared" status is real. Must branch on the
  // same isBrokerOrSubCompanyUser check as the Underwriting Conditions tab (Conditions.js) — a
  // sub-company user's MASTER company type isn't Broker, so selectIsBrokerCompany alone missed
  // them, sending this widget down the internal loanId branch. That branch's empty result then
  // clobbered the Conditions tab's already-populated broker slice (both write the same reducer
  // key), rendering "All 0" (DEV-5396).
  useEffectSafeStrictMode(() => {
    if (!isWidgetVisible) {
      return;
    }

    if (isBrokerOrSubCompanyUser && applicationId) {
      dispatch(getLoanBrokerConditionByApplicationIdAction({ applicationId }));

      return;
    }

    if (!isBrokerOrSubCompanyUser && loanId) {
      dispatch(getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }));
    }
  }, [dispatch, isWidgetVisible, isBrokerOrSubCompanyUser, applicationId, loanId]);

  // Disclosure delivery history backs the "Disclosures" status — fetch it only
  // when the channel actually has a disclosures step.
  useEffectSafeStrictMode(() => {
    if (!isWidgetVisible || !disclosureStepEnabled || !loanId) {
      return;
    }

    dispatch(getLoanBundleHistoryAction({ loanId, applicationId }));
  }, [dispatch, isWidgetVisible, disclosureStepEnabled, loanId, applicationId]);

  // Wholesale loans echo the broker's imported DU/LPA identifiers on the AUS step, so
  // prefetch them for the collapsed chips even before the intake drawer is opened.
  useEffectSafeStrictMode(() => {
    if (isWidgetVisible && isWholesale && loanId) {
      dispatch(getAusCasefilePrefillAction({ companyId, loanId }));
    }
  }, [dispatch, isWidgetVisible, isWholesale, companyId, loanId]);

  const steps = useMemo(() => buildSubmissionSteps(channelConfig), [channelConfig]);

  const areSignalsResolved = useMemo(
    () =>
      areStepSignalsResolved(steps, {
        areCommonInfoRequestsSettled,
        isAusSummarySettled,
        isConditionsSettled,
        isDisclosureHistorySettled,
        isDisclosuresSendActionSettled: !isResolvingDisclosuresSendAction,
        isBrokerOrSubCompanyUser,
        hasLoanId: !!loanId,
        hasApplicationId: !!applicationId,
      }),
    [
      steps,
      areCommonInfoRequestsSettled,
      isAusSummarySettled,
      isConditionsSettled,
      isDisclosureHistorySettled,
      isResolvingDisclosuresSendAction,
      isBrokerOrSubCompanyUser,
      loanId,
      applicationId,
    ],
  );

  // Sealed status: a downstream step cannot read DONE before every upstream required
  // step is done (kills the vacuous "AUS/Conditions done while still at Pricing").
  const stepStatusMap = useMemo(() => buildStepStatusMap(steps, signals), [steps, signals]);

  const { doneCount, blockingCount } = useMemo(
    () => countStepProgress(steps, stepStatusMap),
    [steps, stepStatusMap],
  );

  const activeStepKey = useMemo(
    () => deriveActiveStepKey(steps, stepStatusMap),
    [steps, stepStatusMap],
  );
  const activeStep = useMemo(
    () => steps.find(step => step.key === activeStepKey) ?? null,
    [steps, activeStepKey],
  );
  const railSteps = useMemo(
    () => buildRailSteps(steps, activeStepKey, stepStatusMap),
    [steps, activeStepKey, stepStatusMap],
  );
  const nextAction = useMemo(() => buildNextAction(activeStep), [activeStep]);
  const donePercent = computeDonePercent(doneCount, steps.length);

  const terminalLoanActionType = channelConfig
    ? TERMINAL_ACTION_LOAN_ACTION[channelConfig.terminalAction]
    : null;
  const terminalLoanAction = useMemo(
    () =>
      terminalLoanActionType ? findLoanActionByType(loanActions, terminalLoanActionType) : null,
    [loanActions, terminalLoanActionType],
  );
  // Presence-only resolve — a disabled LockLoan action still resolves here; the provider's
  // disabled-set guard surfaces such a trigger as a warning toast instead of executing it
  // (LoanActionsControlsProvider), so the click is never silent.
  const lockLoanAction = useMemo(
    () => findLoanActionByType(loanActions, LoanEditorActionsEnum.LOCK_LOAN),
    [loanActions],
  );

  const triggerLoanAction = useCallback(loanAction => {
    if (!loanAction) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent(LOAN_ACTION_TRIGGER_EVENT, {
        detail: { actionId: loanAction.id, actionName: loanAction.name },
      }),
    );
  }, []);

  const onNavigateToTab = useCallback(
    activeTab => dispatch(setActiveTabWithNavigationAction({ activeTab, applicationId })),
    [dispatch, applicationId],
  );

  const onNavigateToPricing = useCallback(
    () =>
      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: LoanApplicationTabName.PROPERTY_AND_LOAN,
          activeSubtab: ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK,
          entityId: LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING,
          applicationId,
        }),
      ),
    [dispatch, applicationId],
  );

  const onNavigateToLockInfo = useCallback(
    () =>
      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: LoanApplicationTabName.PROPERTY_AND_LOAN,
          activeSubtab: ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK,
          entityId: LOAN_AND_LOCK_SECTION_HREF_MAP.LOCK_INFO,
          applicationId,
        }),
      ),
    [dispatch, applicationId],
  );

  const onViewDisclosures = useCallback(
    () =>
      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: LoanApplicationTabName.DOCUMENTS,
          activeSubtab: DocumentsSubtabEnum.DISCLOSURES,
          applicationId,
        }),
      ),
    [dispatch, applicationId],
  );

  const onPrepareDisclosures = useCallback(
    () => dispatch(openPrepareDisclosuresWizardAction()),
    [dispatch],
  );

  // Until the package is sent, the disclosures step is an ACTION — it opens the Prepare Disclosures
  // wizard (the same drawer the header CTA opens). Once sent, the Documents > Disclosures subtab is
  // the right destination: it tracks per-signer delivery. A user with no send action on this loan,
  // or a tenant with the wizard disabled via company property, can only view.
  const disclosuresStepCta = useMemo(
    () =>
      !isDisclosureSent && disclosuresSendAction && !isPrepareDisclosuresWizardDisabled
        ? { label: 'Prepare Disclosures', onClick: onPrepareDisclosures }
        : { label: 'View Disclosures', onClick: onViewDisclosures },
    [
      isDisclosureSent,
      disclosuresSendAction,
      isPrepareDisclosuresWizardDisabled,
      onPrepareDisclosures,
      onViewDisclosures,
    ],
  );

  const onTerminalAction = useCallback(
    () => triggerLoanAction(terminalLoanAction),
    [triggerLoanAction, terminalLoanAction],
  );

  const onToggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);

  const onOpenCasefileDrawer = useCallback(() => setIsCasefileDrawerOpen(true), []);
  const onCloseCasefileDrawer = useCallback(() => setIsCasefileDrawerOpen(false), []);

  const secondaryCta = useMemo(
    () => ({ label: isExpanded ? 'Hide steps' : 'View all steps', onClick: onToggleExpanded }),
    [isExpanded, onToggleExpanded],
  );

  // Wholesale loans provide the broker's casefile IDs via the intake drawer instead of
  // navigating to Underwriting to run a fresh AUS submission.
  const ausStepCta = useMemo(
    () =>
      isWholesale
        ? { label: WHOLESALE_AUS_CTA_LABEL, onClick: onOpenCasefileDrawer }
        : {
            label: 'Go to Underwriting',
            onClick: () => onNavigateToTab(LoanApplicationTabName.UNDERWRITING_NEW),
          },
    [isWholesale, onOpenCasefileDrawer, onNavigateToTab],
  );

  const stepCtaHandlers = useMemo(
    () => ({
      [STEP_KEY.PRICING]: {
        label: 'Go to Pricing',
        onClick: onNavigateToPricing,
      },
      // When no LockLoan action resolves on this loan (not configured / not visible to this
      // user), a "Lock Loan" button would silently no-op — relabel it to an honest navigation
      // CTA targeting the Lock Info section instead.
      [STEP_KEY.LOCK]: lockLoanAction
        ? { label: 'Lock Loan', onClick: () => triggerLoanAction(lockLoanAction) }
        : { label: 'Go to Lock Info', onClick: onNavigateToLockInfo },
      [STEP_KEY.AUS]: ausStepCta,
      [STEP_KEY.DOCUMENTS_CONDITIONS]: {
        label: 'Go to Conditions',
        onClick: () => onNavigateToTab(LoanApplicationTabName.UNDERWRITING_NEW),
      },
      [STEP_KEY.DISCLOSURES]: disclosuresStepCta,
    }),
    [
      onNavigateToTab,
      onNavigateToPricing,
      onNavigateToLockInfo,
      disclosuresStepCta,
      lockLoanAction,
      triggerLoanAction,
      ausStepCta,
    ],
  );

  // The backend's aggregated per-leg error, split so a failed leg renders its own error chip
  // (and its retry names the failed leg). Null unless the latest AUS operation errored.
  const ausLegErrors = useMemo(
    () => (ausSubmitError ? parseAusLegErrors(ausSubmitError.message) : null),
    [ausSubmitError],
  );

  const wholesaleAusChips = useMemo(
    () =>
      isWholesale
        ? buildWholesaleAusChips({
            casefileIntake,
            ausSummary,
            isSubmitting: isAusSubmitting,
            legErrors: ausLegErrors,
          })
        : [],
    [isWholesale, casefileIntake, ausSummary, isAusSubmitting, ausLegErrors],
  );

  const terminalActionLabel = channelConfig?.terminalAction
    ? TERMINAL_ACTION_BUTTON_LABEL[channelConfig.terminalAction]
    : null;

  // The terminal action stays disabled until every blocking step clears and the
  // channel's terminal loan-action is actually available on this loan.
  const isTerminalEnabled = blockingCount === 0 && !!terminalLoanAction;

  // The callout always points the broker at the next step to act on.
  const primaryCta = useMemo(
    () => (activeStep ? (stepCtaHandlers[activeStep.key] ?? null) : null),
    [activeStep, stepCtaHandlers],
  );

  const isWholesaleAusActive = isWholesale && activeStep?.key === STEP_KEY.AUS;

  // A wholesale AUS retrieval that is in flight or has errored owns the callout even when a stale
  // prior summary already marked the AUS step Done (so it is no longer the active step) — mockup-6
  // surfaces that error+retry over the "Ready to submit" prompt so the failure isn't invisible.
  const isWholesaleAusFocused =
    isWholesaleAusActive || (isWholesale && (isAusSubmitting || !!ausSubmitError));

  // Per-leg chips otherwise render only inside the expanded step rows; surface them under the
  // collapsed callout too whenever the wholesale AUS step is in play (active/in-progress/error)
  // or already done, so per-leg status is visible without expanding. Never for retail.
  const showWholesaleAusChips = isWholesale && (isWholesaleAusActive || isAusDone);

  // Re-run the wholesale submit for the failed leg(s) only, reusing the stored casefile IDs.
  // An unattributable error retries both legs.
  const onRetryAusSubmit = useCallback(() => {
    const hasAttributedLeg = !!(ausLegErrors?.[AUS_LEG.DU] || ausLegErrors?.[AUS_LEG.LP]);
    const retryDu = !hasAttributedLeg || !!ausLegErrors[AUS_LEG.DU];
    const retryLp = !hasAttributedLeg || !!ausLegErrors[AUS_LEG.LP];

    dispatch(
      startWholesaleAusSubmitAction({
        companyId,
        loanId,
        duCasefileId: retryDu ? casefileIntake.duCasefileId : null,
        lpAusKey: retryLp ? casefileIntake.lpAusKey : null,
      }),
    ).catch(Error);
  }, [dispatch, ausLegErrors, companyId, loanId, casefileIntake]);

  // While a wholesale AUS retrieval is running or has errored, the AUS step's callout swaps
  // its copy + CTAs; otherwise it keeps the generic next-action prompt.
  const ausCalloutOverride = useMemo(() => {
    if (!isWholesaleAusFocused) {
      return null;
    }

    if (isAusSubmitting) {
      return {
        nextAction: {
          severity: NEXT_ACTION_SEVERITY.IN_PROGRESS,
          title: WHOLESALE_AUS_IN_PROGRESS_TITLE,
          description: buildWholesaleAusInProgressDescription(casefileIntake),
        },
        primaryCta: null,
        secondaryCta,
      };
    }

    if (ausSubmitError) {
      return {
        nextAction: {
          severity: NEXT_ACTION_SEVERITY.BLOCKING,
          title: WHOLESALE_AUS_ERROR_TITLE,
          description: ausSubmitError.message ?? WHOLESALE_AUS_ERROR_FALLBACK_DESCRIPTION,
        },
        primaryCta: { label: buildAusRetryLabel(ausLegErrors), onClick: onRetryAusSubmit },
        secondaryCta: { label: WHOLESALE_AUS_EDIT_CTA_LABEL, onClick: onOpenCasefileDrawer },
      };
    }

    return null;
  }, [
    isWholesaleAusFocused,
    isAusSubmitting,
    ausSubmitError,
    ausLegErrors,
    casefileIntake,
    secondaryCta,
    onRetryAusSubmit,
    onOpenCasefileDrawer,
  ]);

  // On a wholesale loan the AUS step's next-action reads as a casefile-intake prompt
  // rather than the generic "run AUS" copy; the in-progress/error override wins when present.
  const resolvedNextAction =
    ausCalloutOverride?.nextAction ??
    (isWholesaleAusActive ? { ...nextAction, ...WHOLESALE_AUS_NEXT_ACTION } : nextAction);

  const resolvedPrimaryCta = ausCalloutOverride ? ausCalloutOverride.primaryCta : primaryCta;
  const resolvedSecondaryCta = ausCalloutOverride ? ausCalloutOverride.secondaryCta : secondaryCta;

  const onStepClick = useCallback(
    stepKey => {
      const handler = stepCtaHandlers[stepKey];

      if (handler) {
        handler.onClick();
      }
    },
    [stepCtaHandlers],
  );

  if (!isWidgetVisible) {
    return null;
  }

  const subtitle = terminalActionLabel
    ? `${channelConfig.name} · ${terminalActionLabel}`
    : channelConfig.name;

  if (!areSignalsResolved) {
    return <PathToSubmissionSkeleton steps={steps} subtitle={subtitle} />;
  }

  return (
    <Box sx={widgetStyles}>
      <PathToSubmissionHeader subtitle={subtitle}>
        <Box sx={progressTextGroupStyles}>
          <Typography variant="medium12">
            {doneCount} of {steps.length} steps · {donePercent}%
          </Typography>
          <SubmissionProgressBar percent={donePercent} />
        </Box>
        {terminalActionLabel && (
          <Button
            data-testid={PathToSubmissionTestIds.TERMINAL_ACTION_BUTTON}
            disabled={!isTerminalEnabled}
            size="small"
            sx={terminalButtonStyles}
            variant="contained"
            onClick={onTerminalAction}
          >
            {terminalActionLabel}
          </Button>
        )}
        <Button
          data-testid={PathToSubmissionTestIds.TOGGLE_BUTTON}
          size="small"
          sx={getChevronButtonStyles(isExpanded)}
          variant="outlined"
          onClick={onToggleExpanded}
        >
          <ArrowIcon size={14} />
        </Button>
      </PathToSubmissionHeader>

      <SubmissionStepper railSteps={railSteps} onStepClick={onStepClick} />

      <Box sx={calloutSectionStyles}>
        <NextActionCallout
          description={resolvedNextAction.description}
          primaryCta={resolvedPrimaryCta}
          secondaryCta={resolvedSecondaryCta}
          severity={resolvedNextAction.severity}
          title={resolvedNextAction.title}
        />
        {showWholesaleAusChips && wholesaleAusChips.length > 0 && (
          <Box data-testid={PathToSubmissionTestIds.CALLOUT_CHIPS_ROW} sx={calloutChipsRowStyles}>
            {wholesaleAusChips.map(chip => (
              <LabelCDS key={chip.key} size="S" title={chip.title} variant={chip.variant} />
            ))}
          </Box>
        )}
      </Box>

      {isExpanded && steps.length > 0 && (
        <Box sx={detailSectionStyles}>
          {steps.map(step => {
            const isWholesaleAusStep = isWholesale && step.key === STEP_KEY.AUS;

            return (
              <SubmissionStepRow
                badge={isWholesaleAusStep ? WHOLESALE_AUS_BADGE : step.badge}
                chips={isWholesaleAusStep ? wholesaleAusChips : EMPTY_CHIPS}
                cta={stepCtaHandlers[step.key] ?? null}
                key={step.key}
                statusType={stepStatusMap[step.key]}
                subtitle={isWholesaleAusStep ? WHOLESALE_AUS_SUBTITLE : STEP_SUBTITLE[step.key]}
                title={STEP_TITLE[step.key]}
              />
            );
          })}
        </Box>
      )}

      {isWholesale && (
        <AusCasefileIntakeDrawer
          companyId={companyId}
          isOpen={isCasefileDrawerOpen}
          loanId={loanId}
          onClose={onCloseCasefileDrawer}
        />
      )}
    </Box>
  );
};

export default PathToSubmission;
